import { CircularProgress, Divider, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { FormikConfig } from 'formik';
import React from 'react';
import Details from '../../../components/Details/Details';
import FilterComponentV2 from '../../../components/TablegridWithFilters/FilterComponent/FilterComponentv2';
import TabsLayout from '../../../components/Tabs/TabsLayout';
import { paidParavolaInfo, refundParavolaInfo, usedParavolaInfo } from '../../../lib/fetchers';
import { eliminateOjectNotTruthyValues, matchRefsWithData } from '../../../lib/helperFuncs';
import { paidParavolaRefs, refundParavolaRefs, usedParavolaRefs } from '../../../lib/static/details/staticDetails';
import { retrieveParavolaInfoFields } from '../../../lib/static/filters/filterFields';
import { retrieveParavolaInfoInitialValues } from '../../../lib/static/filters/initialValues';
import { paravolaInfoValidationSchema } from '../../../lib/static/filters/ValidationSchema';
import Theme from '../../../Themes/main.theme';

const ParavolaInfo = () => {
  const [tab, setTab] = React.useState(0);
  const [dataPaid, setDataPaid] = React.useState();
  const [dataUsed, setDataUsed] = React.useState();
  const [dataRefund, setDataRefund] = React.useState();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();

  const tabLabels = [
    {
      label: <Typography variant='h4'>Πληρωμένα παράβολα</Typography>,
    },
    {
      label: <Typography variant='h4'>Δεσμευμένα παράβολα</Typography>,
    },
    {
      label: <Typography variant='h4'>Παράβολα με μερική ή ολική επιστροφή</Typography>,
    }
  ];

  const onSubmitPaid = async (values: FormikConfig<any>) => {    
    setIsLoading(true);
    try {
      const response = await paidParavolaInfo(eliminateOjectNotTruthyValues(values));
      setError('');
      setDataPaid(response.data);
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 400) {
        setError(error.response.data.message);
      }
    }
    setIsLoading(false);
  };

  const onSubmitUsed = async (values: FormikConfig<any>) => {    
    setIsLoading(true);
    try {
      const response = await usedParavolaInfo(eliminateOjectNotTruthyValues(values));
      setError('');
      setDataUsed(response.data);
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 400) {
        setError(error.response.data.message);
      }
    }
    setIsLoading(false);
  };

  const onSubmitRefund = async (values: FormikConfig<any>) => {    
    setIsLoading(true);
    try {
      const response = await refundParavolaInfo(eliminateOjectNotTruthyValues(values));
      setError('');
      setDataRefund(response.data);
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 400) {
        setError(error.response.data.message);
      }
    }
    setIsLoading(false);
  };

  const tab1 = <Box key='PaidParavola' mt={2}>
    <FilterComponentV2
      title='Φίλτρο Αναζήτησης Πληρωμένων Παραβόλων'
      validationSchema={paravolaInfoValidationSchema}
      filterFields={retrieveParavolaInfoFields}
      initialValues={retrieveParavolaInfoInitialValues}
      resetFilters={true}
      changeFilters={filterValues => onSubmitPaid(filterValues)}
      validateOnBlur={false}
    />
    <Box mt={5}>
      {isLoading
        ? <Box sx={{ display: 'flex', justifyContent: 'center'}}>
          <CircularProgress />
        </Box>
        : error
          ? <Typography variant='h4' display='flex'
            justifyContent='center' >
            {error}
          </Typography> 
          : dataPaid
            ? <Details rows={matchRefsWithData(dataPaid, paidParavolaRefs)}/>
            : null 
      }
    </Box>
  </Box>;

  const tab2 = <Box key='usedParavola' mt={2}>
    <FilterComponentV2
      title='Φίλτρο Αναζήτησης Δεσμευμένων Παραβόλων'
      validationSchema={paravolaInfoValidationSchema}
      filterFields={retrieveParavolaInfoFields}
      initialValues={retrieveParavolaInfoInitialValues}
      resetFilters={true}
      changeFilters={filterValues => onSubmitUsed(filterValues)}
      validateOnBlur={false}
    />
    <Box mt={5}>
      {isLoading
        ? <Box sx={{ display: 'flex', justifyContent: 'center'}}>
          <CircularProgress />
        </Box>
        : error
          ? <Typography variant='h4' display='flex'
            justifyContent='center' >
            {error}
          </Typography> 
          : dataUsed
            ? <Details rows={matchRefsWithData(dataUsed, usedParavolaRefs)}/>
            : null 
      }
    </Box>
  </Box>;

  const tab3 = <Box key='refundParavola' mt={2}>
    <FilterComponentV2
      title='Φίλτρο Αναζήτησης Παραβόλων Μερικής ή Ολικής Επιστροφής'
      validationSchema={paravolaInfoValidationSchema}
      filterFields={retrieveParavolaInfoFields}
      initialValues={retrieveParavolaInfoInitialValues}
      resetFilters={true}
      changeFilters={filterValues => onSubmitRefund(filterValues)}
      validateOnBlur={false}
    />
    <Box mt={5}>
      {isLoading
        ? <Box sx={{ display: 'flex', justifyContent: 'center'}}>
          <CircularProgress />
        </Box>
        : error
          ? <Typography variant='h4' display='flex'
            justifyContent='center' >
            {error}
          </Typography> 
          : dataRefund
            ? <Details rows={matchRefsWithData(dataRefund, refundParavolaRefs)}/>
            : null 
      }
    </Box>
  </Box>;

  const tabContent = [
    tab1,
    tab2,
    tab3
  ];

  return (
    <Box mt={5}>
      <Typography sx={{ my: 2 }} variant='h2'
        color={Theme?.labelColor?.main}>Στοιχεία Ηλεκτρονικού Παραβόλου</Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      <TabsLayout
        tab={tab}
        setTab={setTab}
        tabLabels={tabLabels}
        tabContent={tabContent}
        resetFilters={() => {
          setError('');
          setDataPaid(undefined);
          setDataUsed(undefined);
          setDataRefund(undefined);
        }}
      />
    </Box>
  );
};

export default ParavolaInfo;